'use client';

import { ListTodo } from 'lucide-react';
import Chip, { type ChipProps } from '~/components/Chip';
import Skeleton from '~/components/Loading/Skeleton';
import PendingLink from '~/components/Navigation/PendingLink';
import { SettingsEmptyState } from '~/components/settings/SettingsEmptyState';
import { Routes } from '~/config';
import { useWorkspace } from '~/contexts/WorkspaceContext';
import { dateFormatters } from '~/utils/dateUtils';
import { useHomeQueueOverview } from './useHomeQueueOverview';

type HomeRecentReviewsProps = {
  enabled: boolean;
  hideHeader?: boolean;
};

function statusChipLevel(status: string | null | undefined): ChipProps['level'] {
  const value = (status || '').toLowerCase();
  if (value.includes('complete') || value.includes('done') || value.includes('approved')) {
    return 'success';
  }
  if (value.includes('progress') || value.includes('review')) {
    return 'info';
  }
  if (value.includes('backlog') || value.includes('new') || value.includes('request')) {
    return 'warning';
  }
  return 'neutral';
}

function RecentReviewsSkeleton() {
  return (
    <div
      className="border-border bg-card overflow-hidden rounded-xl border shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
      aria-hidden
    >
      <div className="w-full overflow-x-auto px-3 pt-2 pb-1">
        <table className="w-full table-fixed border-collapse text-left">
          <colgroup>
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
          </colgroup>
          <thead>
            <tr className="text-muted-foreground text-[13px]">
              <th className="w-1/4 px-2 pt-1 pb-3 font-medium">Task</th>
              <th className="w-1/4 px-2 pt-1 pb-3 font-medium">Queue</th>
              <th className="w-1/4 px-2 pt-1 pb-3 font-medium">Status</th>
              <th className="w-1/4 px-2 pt-1 pb-3 font-medium">Created</th>
            </tr>
          </thead>
          <tbody>
            {[...Array(5)].map((_, i) => (
              <tr key={i} className="border-border/50 border-t">
                <td className="px-2 py-3.5">
                  <Skeleton className="h-4 w-40" />
                </td>
                <td className="px-2 py-3.5">
                  <Skeleton className="h-4 w-24" />
                </td>
                <td className="px-2 py-3.5">
                  <Skeleton className="h-5 w-16" />
                </td>
                <td className="px-2 py-3.5">
                  <Skeleton className="h-4 w-20" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function HomeRecentReviews({ enabled, hideHeader }: HomeRecentReviewsProps) {
  const { workspaceId } = useWorkspace();
  const { data, isLoading } = useHomeQueueOverview(enabled);

  if (!enabled) return null;

  const queues = data?.queues ?? [];
  const rows = data?.recent ?? [];
  if (!hideHeader && !isLoading && queues.length === 0) return null;

  const busiestQueueId = queues[0]?.id;
  const viewAllHref = busiestQueueId
    ? `/${workspaceId}${Routes.PROJECTS}/${busiestQueueId}/list`
    : `/${workspaceId}${Routes.SETTINGS}/review-queues`;

  const body = isLoading ? (
    <RecentReviewsSkeleton />
  ) : rows.length === 0 ? (
    <SettingsEmptyState
      size="section"
      icon={ListTodo}
      title="No Open Tasks"
      description="New submissions will show up here."
      className="min-h-0 py-4"
    />
  ) : (
    <div className="border-border bg-card overflow-hidden rounded-xl border shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
      <div className="w-full overflow-x-auto px-3 pt-2 pb-1">
        <table className="w-full table-fixed border-collapse text-left">
          <colgroup>
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
          </colgroup>
          <thead>
            <tr className="text-muted-foreground text-[13px]">
              <th className="w-1/4 px-2 pt-1 pb-3 font-medium">Task</th>
              <th className="w-1/4 px-2 pt-1 pb-3 font-medium">Queue</th>
              <th className="w-1/4 px-2 pt-1 pb-3 font-medium">Status</th>
              <th className="w-1/4 px-2 pt-1 pb-3 font-medium">Created</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const href = `/${workspaceId}${Routes.PROJECTS}/${row.remoteTaskManagerId}/list?taskId=${row.id}`;
              return (
                <tr
                  key={row.id}
                  className="border-border/50 hover:bg-muted border-t transition-colors"
                >
                  <td className="overflow-hidden px-2 py-3.5 align-middle">
                    <div className="flex min-w-0 items-center gap-1.5">
                      <PendingLink
                        href={href}
                        className="text-foreground focus-visible:ring-ring/50 min-w-0 truncate text-sm font-medium outline-none focus-visible:ring-[3px]"
                      >
                        {row.name}
                      </PendingLink>
                      {row.highRiskCount > 0 ? (
                        <span
                          className="inline-flex"
                          title={`${row.highRiskCount} high-risk findings`}
                        >
                          <Chip
                            level="alert"
                            text={row.highRiskCount}
                            className="h-5 min-w-[1.375rem] shrink-0 justify-center px-1.5 tabular-nums"
                          />
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="overflow-hidden px-2 py-3.5 align-middle">
                    <span className="text-muted-foreground block truncate text-sm">
                      {row.queueName}
                    </span>
                  </td>
                  <td className="overflow-hidden px-2 py-3.5 align-middle">
                    {row.remoteTaskStatus ? (
                      <Chip
                        level={statusChipLevel(row.remoteTaskStatus)}
                        text={row.remoteTaskStatus}
                        className="max-w-full truncate"
                      />
                    ) : (
                      <span className="text-muted-foreground/50 text-sm">—</span>
                    )}
                  </td>
                  <td className="overflow-hidden px-2 py-3.5 align-middle whitespace-nowrap">
                    <span
                      className="text-muted-foreground block truncate text-sm"
                      title={dateFormatters.fullDateTime(row.createdAt)}
                    >
                      {dateFormatters.relativeTime(row.createdAt)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (hideHeader) return body;

  return (
    <section aria-labelledby="home-recent-tasks-heading">
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <h2
          id="home-recent-tasks-heading"
          className="text-foreground text-sm font-semibold tracking-tight"
        >
          Recent Tasks
        </h2>
        <PendingLink
          href={viewAllHref}
          className="text-muted-foreground hover:text-foreground focus-visible:ring-ring/50 text-xs outline-none focus-visible:ring-[3px]"
        >
          View all
        </PendingLink>
      </div>
      {body}
    </section>
  );
}
